<template>
  <div class="accordion-widget" :style="widgetStyles">
    <div v-if="widget.title" class="accordion-title mb-3">
      <h3 :style="titleStyles">{{ widget.title }}</h3>
    </div>

    <div class="accordion" :id="`accordion-${widget.id}`">
      <div 
        v-for="(item, index) in widget.items" 
        :key="item.id"
        class="accordion-item"
        :class="accordionItemClass"
      >
        <h2 class="accordion-header" :id="`heading-${widget.id}-${item.id}`">
          <button 
            class="accordion-button"
            :class="{ 'collapsed': !isItemOpen(item.id) }"
            type="button"
            @click="toggleItem(item.id)"
            :aria-expanded="isItemOpen(item.id)"
            :aria-controls="`collapse-${widget.id}-${item.id}`"
          >
            <i v-if="widget.showIcons" :class="getIcon(index)" class="me-2"></i>
            <span>{{ item.title }}</span>
          </button>
        </h2>
        <Transition name="accordion">
          <div 
            v-show="isItemOpen(item.id)"
            :id="`collapse-${widget.id}-${item.id}`"
            class="accordion-collapse"
            :aria-labelledby="`heading-${widget.id}-${item.id}`"
          >
            <div
              class="accordion-body"
              :style="contentStyles"
              v-html="processedContent(item.content)"
            >
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { AccordionWidget } from '@/types/pagebuilder'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'
import { processRichTextContent } from '@/utils/textWidgetUtils'

interface Props {
  widget: AccordionWidget
}

const props = defineProps<Props>()

// Track open items
const openItems = ref<Set<string>>(new Set())

// Initialize with default open items
if (props.widget.items) {
  props.widget.items.forEach(item => {
    if (item.isOpen) {
      openItems.value.add(item.id)
    }
  })
}

// Computed styles
const widgetStyles = computed(() => ({
  padding: props.widget.padding || '1rem 0',
  ...resolveWidgetBackgroundStyle(props.widget),
}))

const titleSizeMap: Record<string, string> = { sm: '1.1rem', md: '1.5rem', lg: '2rem' }

const titleStyles = computed(() => {
  const rawSize = props.widget.titleSize || 'md'
  const fontSize = titleSizeMap[rawSize] || rawSize
  return {
    color: props.widget.titleColor || '#333',
    fontSize,
    textAlign: props.widget.titleAlignment || 'left',
    margin: '0 0 1rem 0'
  }
})

const contentColor = computed(
  () => props.widget.contentColor || props.widget.textColor || '#666',
)

const contentStyles = computed(() => ({
  color: contentColor.value,
  '--widget-text-color': contentColor.value,
}))

const accordionItemClass = computed(() => {
  const style = props.widget.style || 'default'
  return {
    'accordion-bordered': style === 'bordered',
    'accordion-filled': style === 'filled'
  }
})

// Methods
const isItemOpen = (itemId: string): boolean => {
  return openItems.value.has(itemId)
}

const toggleItem = (itemId: string) => {
  if (!props.widget.allowMultiple) {
    // Close all other items if multiple not allowed
    openItems.value.clear()
  }
  
  if (openItems.value.has(itemId)) {
    openItems.value.delete(itemId)
  } else {
    openItems.value.add(itemId)
  }
}

const getIcon = (index: number): string => {
  const icons = [
    'bi bi-1-circle-fill',
    'bi bi-2-circle-fill',
    'bi bi-3-circle-fill',
    'bi bi-4-circle-fill',
    'bi bi-5-circle-fill',
    'bi bi-6-circle-fill',
    'bi bi-7-circle-fill',
    'bi bi-8-circle-fill',
    'bi bi-9-circle-fill'
  ]
  return icons[index] || 'bi bi-circle-fill'
}

const processedContent = (content: string): string => {
  return processRichTextContent(content, contentColor.value)
}
</script>

<style scoped>
.accordion-widget {
  max-width: 100%;
}

.accordion-title h3 {
  margin: 0;
  font-weight: 600;
}

.accordion {
  --bs-accordion-color: #212529;
  --bs-accordion-bg: #fff;
  --bs-accordion-border-color: #dee2e6;
  --bs-accordion-border-width: 1px;
  --bs-accordion-border-radius: 0.375rem;
  --bs-accordion-inner-border-radius: calc(0.375rem - 1px);
  --bs-accordion-btn-padding-x: 1.25rem;
  --bs-accordion-btn-padding-y: 1rem;
  --bs-accordion-btn-color: #212529;
  --bs-accordion-btn-bg: #f8f9fa;
  --bs-accordion-btn-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
  --bs-accordion-btn-icon-width: 1.25rem;
  --bs-accordion-btn-icon-transform: rotate(-180deg);
  --bs-accordion-btn-icon-transition: transform 0.2s ease-in-out;
  --bs-accordion-btn-active-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23052c65'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
  --bs-accordion-btn-focus-border-color: #86b7fe;
  --bs-accordion-btn-focus-box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  --bs-accordion-body-padding-x: 1.25rem;
  --bs-accordion-body-padding-y: 1rem;
}

.accordion-item {
  color: var(--bs-accordion-color);
  background-color: var(--bs-accordion-bg);
  border: var(--bs-accordion-border-width) solid var(--bs-accordion-border-color);
}

.accordion-item:first-of-type {
  border-top-left-radius: var(--bs-accordion-border-radius);
  border-top-right-radius: var(--bs-accordion-border-radius);
}

.accordion-item:first-of-type > .accordion-header .accordion-button {
  border-top-left-radius: var(--bs-accordion-inner-border-radius);
  border-top-right-radius: var(--bs-accordion-inner-border-radius);
}

.accordion-item:not(:first-of-type) {
  border-top: 0;
}

.accordion-item:last-of-type {
  border-bottom-right-radius: var(--bs-accordion-border-radius);
  border-bottom-left-radius: var(--bs-accordion-border-radius);
}

.accordion-item:last-of-type > .accordion-header .accordion-button.collapsed {
  border-bottom-right-radius: var(--bs-accordion-inner-border-radius);
  border-bottom-left-radius: var(--bs-accordion-inner-border-radius);
}

.accordion-item:last-of-type > .accordion-collapse {
  border-bottom-right-radius: var(--bs-accordion-border-radius);
  border-bottom-left-radius: var(--bs-accordion-border-radius);
}

.accordion-header {
  margin-bottom: 0;
}

.accordion-button {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--bs-accordion-btn-padding-y) var(--bs-accordion-btn-padding-x);
  font-size: 1rem;
  color: var(--bs-accordion-btn-color);
  text-align: left;
  background-color: var(--bs-accordion-btn-bg);
  border: 0;
  border-radius: 0;
  overflow-anchor: none;
  transition: var(--bs-accordion-btn-icon-transition);
  font-weight: 500;
  cursor: pointer;
}

.accordion-button:not(.collapsed) {
  color: var(--bs-accordion-btn-active-color, #0c63e4);
  background-color: var(--bs-accordion-btn-active-bg, #e7f1ff);
  box-shadow: inset 0 calc(-1 * var(--bs-accordion-border-width)) 0 var(--bs-accordion-border-color);
}

.accordion-button:not(.collapsed)::after {
  background-image: var(--bs-accordion-btn-active-icon);
  transform: var(--bs-accordion-btn-icon-transform);
}

.accordion-button::after {
  flex-shrink: 0;
  width: var(--bs-accordion-btn-icon-width);
  height: var(--bs-accordion-btn-icon-width);
  margin-left: auto;
  content: "";
  background-image: var(--bs-accordion-btn-icon);
  background-repeat: no-repeat;
  background-size: var(--bs-accordion-btn-icon-width);
  transition: var(--bs-accordion-btn-icon-transition);
}

.accordion-button:hover {
  z-index: 2;
}

.accordion-button:focus {
  z-index: 3;
  border-color: var(--bs-accordion-btn-focus-border-color);
  outline: 0;
  box-shadow: var(--bs-accordion-btn-focus-box-shadow);
}

.accordion-collapse {
  transition: height 0.35s ease;
}

.accordion-body {
  padding: var(--bs-accordion-body-padding-y) var(--bs-accordion-body-padding-x);
  line-height: 1.6;
}

.accordion-body :deep(p),
.accordion-body :deep(h1),
.accordion-body :deep(h2),
.accordion-body :deep(h3),
.accordion-body :deep(h4),
.accordion-body :deep(h5),
.accordion-body :deep(h6),
.accordion-body :deep(li),
.accordion-body :deep(span),
.accordion-body :deep(div),
.accordion-body :deep(blockquote),
.accordion-body :deep(strong),
.accordion-body :deep(em) {
  color: var(--widget-text-color, inherit) !important;
}

/* Style variations */
.accordion-bordered .accordion-item {
  border-width: 2px;
  border-color: #007bff;
  margin-bottom: 0.5rem;
  border-radius: 0.375rem !important;
}

.accordion-bordered .accordion-button {
  background-color: transparent;
  border-bottom: 1px solid #dee2e6;
}

.accordion-bordered .accordion-button:not(.collapsed) {
  background-color: #f8f9ff;
}

.accordion-filled .accordion-item {
  background-color: #f8f9fa;
  border: none;
  margin-bottom: 0.5rem;
  border-radius: 0.375rem !important;
}

.accordion-filled .accordion-button {
  background-color: #e9ecef;
}

.accordion-filled .accordion-button:not(.collapsed) {
  background-color: #007bff;
  color: white;
}

.accordion-filled .accordion-button:not(.collapsed)::after {
  filter: brightness(0) invert(1);
}

/* Smooth transitions */
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 1000px;
  opacity: 1;
  transform: translateY(0);
}

/* Icon styling */
.accordion-button i {
  transition: all 0.3s ease;
}

.accordion-button:not(.collapsed) i {
  transform: scale(1.1);
  color: #0c63e4;
}

/* Hover effect */
.accordion-button:hover {
  background-color: #f0f2f5 !important;
}

.accordion-button:not(.collapsed):hover {
  background-color: #d9e7ff !important;
}

/* Responsive */
@media (max-width: 768px) {
  .accordion-button {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
  
  .accordion-body {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
}
</style>